import { useState, useEffect, lazy, Suspense } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence, MotionConfig } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import Dashboard from './components/Dashboard'
import MobileSidebar from './components/MobileSidebar'
import ChatPanel from './components/AiAssistant/ChatPanel'
import PullToRefresh from './components/PullToRefresh'
import { usePullToRefresh } from './hooks/usePullToRefresh'
import { useNotificationBadge } from './hooks/useNotificationBadge'
import { useCategorySettings } from './hooks/useCategorySettings'
import { useNewEventToast } from './hooks/useNewEventToast'
import { EventToastPortal } from './components/EventToast'
import ErrorBoundary from './components/ErrorBoundary'
import { EASE_OUT, EASE_IN } from './theme/motion'
import { EMILIA_PAGE_IDS } from './config/emilia'

// The drawer opens on demand; importing it statically pulled the whole timeline card
// into the entry chunk and defeated the card's own lazy import.
const TimelineDrawer       = lazy(() => import('./components/TimelineCard').then(m => ({ default: m.TimelineDrawer })))
const GenericCardPage      = lazy(() => import('./pages/GenericCardPage'))
const MyDashboardPage      = lazy(() => import('./pages/MyDashboardPage'))
const NotificationsPage    = lazy(() => import('./pages/NotificationsPage'))
const DocsPage             = lazy(() => import('./pages/DocsPage'))
const AutomationsPage      = lazy(() => import('./pages/AutomationsPage'))
const SystemsPage          = lazy(() => import('./pages/SystemsPage'))
const DashboardSettingsPage = lazy(() => import('./pages/DashboardSettingsPage'))
const DesignSystemPage      = lazy(() => import('./pages/DesignSystemPage'))
const FormulasPage          = lazy(() => import('./pages/FormulasPage'))
const HardwarePage          = lazy(() => import('./pages/HardwarePage'))
const McpToolsPage          = lazy(() => import('./pages/McpToolsPage'))
const EnergyFlowPage        = lazy(() => import('./pages/EnergyFlowPage'))
const WiringPage            = lazy(() => import('./pages/WiringPage'))
const HouseMapPage          = lazy(() => import('./pages/HouseMapPage'))
const ReportPage            = lazy(() => import('./pages/ReportPage'))
const FinancePage           = lazy(() => import('./pages/FinancePage'))
const OtherAppsPage         = lazy(() => import('./pages/OtherAppsPage'))
const ExtensionsPage        = lazy(() => import('./pages/ExtensionsPage'))
const EmiliaPage            = lazy(() => import('./pages/EmiliaPage'))

const pathToId = {
  '/': 'my-dashboard',
  '/my-dashboard': 'my-dashboard',
  '/home': 'home',
  '/house': 'house-map',
  '/report': 'report',
  '/report/finanzen': 'report-finance',
  '/energy': 'energy',
  '/energy/flow': 'energy-flow',
  '/energy/wiring': 'energy-wiring',
  '/vehicles': 'vehicles',
  '/appliances': 'appliances',
  '/rooms': 'rooms',
  '/security': 'security',
  '/emilia': 'emilia',
  ...Object.fromEntries(EMILIA_PAGE_IDS.map(id => [`/emilia/${id}`, `emilia-${id}`])),
  '/notifications': 'notifications',
  '/systems': 'systems',
  '/other-apps': 'other-apps',
  '/extensions': 'extensions',
  '/automations': 'logging',
  '/docs': 'docs',
  '/docs/schaltplaene': 'docs-hardware',
  '/docs/mcp': 'docs-mcp',
  '/dashboard-settings': 'dashboard-settings',
  '/design-system': 'design-system',
  '/formulas': 'formulas',
}

function ReloadOnce() {
  useEffect(() => { window.location.reload() }, [])
  return null
}

const idToPath = Object.fromEntries(Object.entries(pathToId).map(([p, id]) => [id, p]))

// Route-level fallback — a failed lazy import is usually a stale chunk after a
// deploy, so a full reload (fresh chunk hashes) is the real recovery, not retry.
function RouteErrorFallback(error, reset) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <span className="material-symbols-outlined" style={{ fontSize: '2rem', color: '#555a54' }}>
        error_outline
      </span>
      <p className="font-label" style={{ fontSize: '13px', color: '#8d928a' }}>
        Seite konnte nicht geladen werden
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => window.location.reload()}
          style={{ background: '#252625', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', color: '#cfd4cc', fontFamily: 'inherit', fontSize: '11px' }}
        >
          Neu laden
        </button>
        <button
          onClick={reset}
          style={{ background: 'transparent', border: '1px solid #2c2d2c', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', color: '#8d928a', fontFamily: 'inherit', fontSize: '11px' }}
        >
          Erneut versuchen
        </button>
      </div>
    </div>
  )
}

export default function App() {
  const location = useLocation()
  const navigate = useNavigate()

  const activeNav = location.pathname.startsWith('/dashboard-settings')
    ? 'dashboard-settings'
    : location.pathname.startsWith('/extensions')
      ? 'extensions'
      : (pathToId[location.pathname] ?? 'my-dashboard')
  const setActiveNav = (id) => navigate(idToPath[id] ?? '/')
  const [fullscreen, setFullscreen] = useState(false)
  const [isTabletMode, setIsTabletMode] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [timelineOpen, setTimelineOpen] = useState(false)
  const { unreadCount, unreadByCategory, markRead } = useNotificationBadge()
  const { settings: catSettings, toggle: toggleCat, hiddenCategories, silentCategories, toastEnabled, toggleToast } = useCategorySettings()
  const { toast, dismiss: dismissToast } = useNewEventToast({ enabled: toastEnabled, silentCategories })

  const effectiveUnreadCount = Object.entries(unreadByCategory)
    .filter(([cat]) => !silentCategories.has(cat))
    .reduce((sum, [, n]) => sum + n, 0)
  const effectiveUnreadByCategory = Object.fromEntries(
    Object.entries(unreadByCategory).filter(([cat]) => !silentCategories.has(cat))
  )

  const qc = useQueryClient()
  const { pullY, refreshing, threshold } = usePullToRefresh({
    onRefresh: async () => {
      document.dispatchEvent(new Event('yokan:refresh'))
      await Promise.all([
        qc.invalidateQueries(),
        new Promise(r => setTimeout(r, 600)),
      ])
    },
  })
  const pullSettling = !refreshing && pullY === 0

  const pages = {
    'my-dashboard': <MyDashboardPage />,
    home: <Dashboard isTabletMode={isTabletMode} />,
    'house-map': <HouseMapPage />,
    report: <ReportPage />,
    'report-finance': <FinancePage />,
    energy:    <GenericCardPage pageKey="energy"     title="Energy"     icon="bolt" />,
    vehicles:  <GenericCardPage pageKey="vehicles"  title="Vehicles"   icon="electric_car" />,
    appliances:<GenericCardPage pageKey="appliances" title="Appliances" icon="wash" />,
    rooms:     <GenericCardPage pageKey="rooms"      title="Rooms"      icon="door_open" />,
    security:  <GenericCardPage pageKey="security"   title="Security"   icon="shield" />,
    emilia: <EmiliaPage />,
    ...Object.fromEntries(EMILIA_PAGE_IDS.map(id => [`emilia-${id}`, <EmiliaPage key={id} pageId={id} />])),
    notifications: <NotificationsPage />,
    systems: <SystemsPage />,
    'other-apps': <OtherAppsPage />,
    extensions: <ExtensionsPage />,
    logging: <AutomationsPage />,
    docs: <DocsPage />,
    'docs-hardware': <HardwarePage />,
    'docs-mcp': <McpToolsPage />,
    'dashboard-settings': <DashboardSettingsPage />,
    'design-system': <DesignSystemPage />,
    formulas: <FormulasPage />,
    'energy-flow': <EnergyFlowPage />,
    'energy-wiring': <WiringPage />,
  }

  return (
    // One switch for the whole tree: with "reduce motion" set in the OS,
    // framer drops transform/layout animations and keeps fades.
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-surface" style={{ fontFamily: "'Be Vietnam Pro', sans-serif" }}>
      <Header activeNav={activeNav} setActiveNav={setActiveNav} fullscreen={fullscreen} isTabletMode={isTabletMode} onTabletToggle={() => setIsTabletMode(v => !v)} onOpenMenu={() => setMobileSidebarOpen(true)} onOpenTimeline={() => setTimelineOpen(true)} unreadCount={effectiveUnreadCount} />
      <PullToRefresh pullY={pullY} refreshing={refreshing} threshold={threshold} />
      <Sidebar activeNav={activeNav} setActiveNav={setActiveNav} fullscreen={fullscreen} />
      <main className={`transition-all duration-500 ${fullscreen ? 'pt-0 pb-0' : 'pt-20 pb-28 md:pb-8 md:pl-14'}`}>
        <div
          className={`mx-auto max-w-screen-2xl ${fullscreen ? 'px-4 py-4 md:px-6 md:py-6' : 'px-4 md:px-8'}`}
          style={{
            transform: `translateY(${pullY}px)`,
            transition: pullSettling || refreshing ? 'transform 0.25s ease' : 'none',
            willChange: 'transform',
          }}
        >
          <ErrorBoundary key={activeNav} fallback={RouteErrorFallback}>
            <Suspense fallback={null}>
              <AnimatePresence mode="wait">
                {/* The page itself only fades and settles — its cards carry the
                    motion (cascade). The old page leaves fast so the new one
                    isn't kept waiting behind mode="wait". */}
                <motion.div
                  key={activeNav}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE_OUT } }}
                  exit={{ opacity: 0, transition: { duration: 0.14, ease: EASE_IN } }}
                >
                  {pages[activeNav] ?? pages['my-dashboard']}
                </motion.div>
              </AnimatePresence>
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      <MobileSidebar open={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} activeNav={activeNav} setActiveNav={setActiveNav} />

      <AnimatePresence>
        {timelineOpen && (
          // A chunk that fails to load is a stale tab after a deploy: reload for fresh hashes.
          <ErrorBoundary key="timeline" fallback={() => <ReloadOnce />}>
          <Suspense fallback={null}>
            <TimelineDrawer onClose={() => setTimelineOpen(false)} unreadByCategory={effectiveUnreadByCategory} onMarkRead={markRead} catSettings={catSettings} onToggleCat={toggleCat} hiddenCategories={hiddenCategories} toastEnabled={toastEnabled} onToggleToast={toggleToast} />
          </Suspense>
          </ErrorBoundary>
        )}
      </AnimatePresence>

      <ChatPanel />

      <EventToastPortal toast={toast} onDismiss={dismissToast} />

      {/* Fullscreen toggle — fixed bottom-right, always visible */}
      <motion.button
        onClick={() => setFullscreen(f => !f)}
        className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200"
        style={{
          background: 'rgba(31,32,31,0.85)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.08)',
          color: 'rgba(195,200,191,0.6)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onMouseEnter={e => e.currentTarget.style.color = '#b0ceae'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(195,200,191,0.6)'}
        title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '1.1rem' }}>
          {fullscreen ? 'fullscreen_exit' : 'fullscreen'}
        </span>
      </motion.button>
    </div>
    </MotionConfig>
  )
}
